import {
  CarWheelPosition as CarWheelPositionEnum,
  CarWheelPositionText as WheelLocation,
  type CarWheelPosition,
  type CarWheelPositionText,
  type InspectionContext,
  type InspectionItemCreateApiData,
  type InspectionItemCreateInput,
  type InspectionSessionCreateApiData,
  type InspectionSessionCreateInput,
  type TireSeason
} from '@navio/nsdk';

import type { ApiInput } from '../../utils';

/**
 * Payloads the worker app still sends until PR 6114 switches those screens
 * onto session items. The web apps already send the session shape.
 */
export type LegacyTireMeasurementsInput = {
  carId: number;
  bookingItemId?: number | null;
  context?: InspectionContext;
  tireMeasurements: Array<{
    wheelPosition: CarWheelPosition;
    treadDepth: { value: number; unit: string };
    season: TireSeason;
  }>;
};

export type LegacyDimensionInput = {
  carId: number;
  bookingItemId?: number | null;
  tireSeasonType: TireSeason;
  carWheelPosition: CarWheelPositionText;
  inspectionContext: InspectionContext;
  tireSpecification: string;
};

type ImageData = File | { uri: string; name: string; type: string };

export type InspectionImageInput = Omit<ApiInput<InspectionItemCreateApiData>, 'image'> & {
  imageData: ImageData;
};

export type LegacyInspectionImageInput = {
  carId: number;
  bookingItemId?: number | null;
  inspectionContext: InspectionContext;
  imageData: { uri: string; name: string; type: string };
  carWheelPosition: CarWheelPositionText;
  tireSeasonType?: TireSeason | null;
};

// Numeric positions are still sent by worker screens on the previous payload.
const WHEEL_ORDER = [
  WheelLocation.FRONT_LEFT,
  WheelLocation.FRONT_RIGHT,
  WheelLocation.REAR_LEFT,
  WheelLocation.REAR_RIGHT
] as const;

const locationFromCarWheelPosition = (position: CarWheelPosition): CarWheelPositionText | undefined => {
  const name = CarWheelPositionEnum[position];
  return (WHEEL_ORDER as readonly string[]).includes(name) ? (name as CarWheelPositionText) : undefined;
};

const isLegacyTireMeasurements = (
  input: ApiInput<InspectionSessionCreateApiData> | LegacyTireMeasurementsInput
): input is LegacyTireMeasurementsInput => 'tireMeasurements' in input;

export const toInspectionSessionBody = (
  input: ApiInput<InspectionSessionCreateApiData> | LegacyTireMeasurementsInput
): InspectionSessionCreateInput => {
  if (!isLegacyTireMeasurements(input)) {
    return input;
  }
  const items = input.tireMeasurements.flatMap((measurement): InspectionItemCreateInput[] => {
    const location = locationFromCarWheelPosition(measurement.wheelPosition);
    if (!location) {
      return [];
    }
    return [
      {
        kind: 'MEASUREMENT',
        location,
        value: measurement.treadDepth,
        measurementType: 'TREAD_DEPTH',
        tire: { season: measurement.season }
      }
    ];
  });
  return {
    carId: input.carId,
    bookingItemId: input.bookingItemId,
    context: input.context ?? 'STANDALONE',
    items
  };
};

const isLegacyDimension = (
  input: ApiInput<InspectionItemCreateApiData> | LegacyDimensionInput
): input is LegacyDimensionInput => 'carWheelPosition' in input;

export const toDimensionBody = (
  input: ApiInput<InspectionItemCreateApiData> | LegacyDimensionInput
): ApiInput<InspectionItemCreateApiData> => {
  if (!isLegacyDimension(input)) {
    return input;
  }
  return {
    carId: input.carId,
    bookingItemId: input.bookingItemId,
    context: input.inspectionContext,
    location: input.carWheelPosition,
    tireSeason: input.tireSeasonType,
    tireSpecification: input.tireSpecification,
    kind: 'SPEC'
  };
};

const isLegacyImage = (input: InspectionImageInput | LegacyInspectionImageInput): input is LegacyInspectionImageInput =>
  'carWheelPosition' in input || 'inspectionContext' in input;

export const toImageInput = (input: InspectionImageInput | LegacyInspectionImageInput): InspectionImageInput => {
  if (!isLegacyImage(input)) {
    return input;
  }
  return {
    carId: input.carId,
    bookingItemId: input.bookingItemId,
    context: input.inspectionContext,
    location: input.carWheelPosition,
    kind: 'PHOTO',
    tireSeason: input.tireSeasonType,
    imageData: input.imageData
  };
};
